/** Lightweight replacements for the lodash helpers used by the date UI. */

export function isNil(value: unknown): value is null | undefined {
  return value == null;
}

export function isNumber(value: unknown): value is number {
  return typeof value === "number";
}

export function isString(value: unknown): value is string {
  return typeof value === "string";
}

export function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

export function includes<T>(
  collection: ArrayLike<T> | null | undefined,
  value: T,
): boolean {
  if (collection == null) {
    return false;
  }
  if (typeof collection === "string") {
    return (collection as string).includes(value as string);
  }

  return Array.prototype.includes.call(collection, value);
}

export function first<T>(
  array: ArrayLike<T> | null | undefined,
): T | undefined {
  if (array == null || array.length === 0) {
    return undefined;
  }

  return array[0];
}

export function last<T>(array: ArrayLike<T> | null | undefined): T | undefined {
  if (array == null || array.length === 0) {
    return undefined;
  }

  return array[array.length - 1];
}

export function indexOf<T>(
  array: ArrayLike<T> | null | undefined,
  value: T,
): number {
  if (array == null) {
    return -1;
  }

  return Array.prototype.indexOf.call(array, value);
}

export function lastIndexOf<T>(
  array: ArrayLike<T> | null | undefined,
  value: T,
): number {
  if (array == null) {
    return -1;
  }

  return Array.prototype.lastIndexOf.call(array, value);
}

export function slice<T>(
  array: ArrayLike<T> | null | undefined,
  start?: number,
  end?: number,
): T[] {
  if (array == null) {
    return [];
  }

  return Array.prototype.slice.call(array, start, end);
}

export function compact<T>(
  array: ArrayLike<T | null | undefined | false | 0 | ""> | null | undefined,
): T[] {
  if (array == null) {
    return [];
  }

  return Array.prototype.filter.call(array, Boolean) as T[];
}

export function uniq<T>(array: ArrayLike<T> | null | undefined): T[] {
  if (array == null) {
    return [];
  }

  return Array.from(new Set(Array.from(array as ArrayLike<T>)));
}

export function concat<T>(
  ...values: Array<T | readonly T[] | null | undefined>
): T[] {
  const result: T[] = [];
  for (const value of values) {
    if (Array.isArray(value)) {
      result.push(...value);
    } else if (value != null) {
      result.push(value as T);
    }
  }

  return result;
}

export function range(start: number, end?: number, step = 1): number[] {
  let from = start;
  let to = end;
  if (to === undefined) {
    to = from;
    from = 0;
  }
  if (step === 0) {
    return [];
  }

  const result: number[] = [];
  if (step > 0) {
    for (let i = from; i < to; i += step) {
      result.push(i);
    }
  } else {
    for (let i = from; i > to; i += step) {
      result.push(i);
    }
  }

  return result;
}

export function sortBy<T>(
  array: ArrayLike<T> | null | undefined,
  iteratee?: ((value: T) => unknown) | keyof T,
): T[] {
  if (array == null) {
    return [];
  }

  const items = Array.from(array as ArrayLike<T>);
  const getCriterion =
    iteratee == null
      ? (value: T) => value
      : typeof iteratee === "function"
        ? iteratee
        : (value: T) => value[iteratee];

  return items.sort((a, b) => {
    const aVal = getCriterion(a) as string | number;
    const bVal = getCriterion(b) as string | number;
    if (aVal > bVal) {
      return 1;
    }
    if (aVal < bVal) {
      return -1;
    }

    return 0;
  });
}

export function filter<T>(
  collection: ArrayLike<T> | null | undefined,
  predicate: (value: T, index: number) => unknown,
): T[] {
  if (collection == null) {
    return [];
  }

  return Array.prototype.filter.call(collection, predicate) as T[];
}

export function find<T>(
  collection: ArrayLike<T> | null | undefined,
  predicate: (value: T, index: number) => unknown,
): T | undefined {
  if (collection == null) {
    return undefined;
  }

  return Array.prototype.find.call(collection, predicate) as T | undefined;
}

export function some<T>(
  collection: ArrayLike<T> | null | undefined,
  predicate: (value: T, index: number) => unknown,
): boolean {
  if (collection == null) {
    return false;
  }

  return Array.prototype.some.call(collection, predicate);
}

/** Call `object[methodName](...args)` when that property is a function. */
export function invoke(
  object: object | null | undefined,
  methodName: string,
  ...args: unknown[]
): unknown {
  if (object == null) {
    return undefined;
  }
  const method = (object as Record<string, unknown>)[methodName];
  if (typeof method === "function") {
    return method.apply(object, args);
  }

  return undefined;
}
